import type {
  CamelCase as CamelCaseString,
  PascalCase as PascalCaseString,
  KebabCase as KebabCaseString,
} from 'scule'
import { pascalCase, kebabCase, camelCase } from 'scule'

export type UnknownRecord = Record<string, any> & {
  length?: never
  call?: never
}

export type IsPlainObject<T> = {
  [K in NonNullable<keyof T>]: T[K] extends (...args: any) => any
    ? 'method'
    : 'data'
}[keyof T] extends 'data'
  ? true
  : false

// maps a object to the same object but with pascal case keys, values stay the same
export type PascalCase<T>
  = IsPlainObject<T> extends true
    ? {
        [K in keyof T as PascalCaseString<K>]: PascalCase<T[K]>
      }
    : T

export type KebabCase<T>
  = IsPlainObject<T> extends true
    ? {
        [K in keyof T as KebabCaseString<K>]: KebabCase<T[K]>
      }
    : T

export type CamelCase<T>
  = IsPlainObject<T> extends true
    ? {
        [K in keyof T as CamelCaseString<K>]: CamelCase<T[K]>
      }
    : T

export const Mapper = {
  transform(
    object: UnknownRecord | [],
    transform: (key: string) => string,
  ): unknown {
    if (
      typeof object !== 'object'
      || object === null
    ) {
      return object
    }

    if (Array.isArray(object)) return object.map(x => this.transform(x, transform))
    if (object.constructor !== Object) return object

    return Object.fromEntries(
      Object.entries(object).map(([key, value]) => [
        transform(key),
        this.transform(value, transform),
      ]),
    )
  },

  toPascalCase<T extends UnknownRecord>(obj?: T) {
    if (!obj) return obj

    return this.transform(obj, pascalCase) as PascalCase<T>
  },

  toKebabCase<T extends UnknownRecord>(obj?: T) {
    if (!obj) return obj

    return this.transform(obj, kebabCase) as KebabCase<T>
  },

  toCamelCase<T extends UnknownRecord>(obj?: T) {
    if (!obj) return obj
    return this.transform(obj, camelCase) as CamelCase<T>
  },
}
